import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import MyULogo, { type MyULogoSize } from '@/shared/ui/MyULogo';

interface MyULoaderProps {
  /** Visible label under the logo. Pass `null` to hide it. */
  label?: string | null;
  /** Stretch to fill the parent (`absolute inset-0`). */
  fullScreen?: boolean;
  /**
   * Compact mode for in-flow placement (list rows, dropdowns, buttons).
   * Hides the backdrop, removes outer padding, and shrinks the logo.
   */
  inline?: boolean;
  /** Override the logo size. Defaults to `lg` (full) / `sm` (inline). */
  logoSize?: MyULogoSize;
  className?: string;
}

/**
 * Branded loading indicator built around the MyU mark.
 *
 * The logo gently pulses and a thin progress bar slides underneath, giving
 * users an unambiguous "MyU is fetching something" cue. This is the canonical
 * loader for every API-bound screen — the legacy `Spinner` component
 * (`src/components/Spinner.tsx`) now forwards to this implementation so every
 * call site stays branded automatically.
 */
export default function MyULoader({
  label,
  fullScreen = false,
  inline = false,
  logoSize,
  className,
}: MyULoaderProps) {
  const { t } = useTranslation('common');
  const resolvedLabel = label === undefined ? t('loading') : label;
  const resolvedLogoSize: MyULogoSize = logoSize ?? (inline ? 'sm' : 'lg');

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={resolvedLabel ?? t('loading')}
      className={cn(
        'flex items-center justify-center',
        inline
          ? 'inline-flex flex-row gap-2 px-1 py-0.5'
          : 'flex-col gap-3 bg-white/80 backdrop-blur-sm',
        !inline && (fullScreen ? 'absolute inset-0 z-40' : 'h-full w-full p-6'),
        className,
      )}
    >
      <MyULogo
        variant="mark"
        size={resolvedLogoSize}
        className={cn('animate-pulse drop-shadow-sm', inline && 'h-5 w-5')}
      />
      {!inline ? (
        <div className="h-1 w-32 overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
          <div className="myu-loader-bar h-full w-1/3 rounded-full bg-brand-emphasis" />
        </div>
      ) : null}
      {resolvedLabel ? (
        <p className={cn(inline ? 'text-xs text-gray-500' : 'text-sm font-medium text-gray-600')}>
          {resolvedLabel}
        </p>
      ) : null}
    </div>
  );
}
